<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="620" scrollable :fullscreen="$vuetify.breakpoint.mobile">
    <v-card v-if="fila">
      <v-card-title class="text-subtitle-1">
        <v-btn icon small :disabled="!fila_prec" @click="$emit('naviga-fila', fila_prec)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-icon small class="mx-1">mdi-view-sequential</v-icon>
        Fila {{ fila.numero }}
        <span class="ml-2 caption grey--text">({{ fila.num_vasi }} vasi)</span>
        <v-btn icon small :disabled="!fila_succ" @click="$emit('naviga-fila', fila_succ)" class="ml-1">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-4">
        <!-- Piazzamenti esistenti -->
        <div class="text-body-2 font-weight-medium mb-2">Piazzamenti esistenti</div>
        <v-alert v-if="!snapshot_fila.length" type="info" outlined dense>
          Nessun prodotto piazzato in questa fila.
        </v-alert>
        <v-list v-else dense class="mb-4 rounded-lg" outlined>
          <v-list-item
            v-for="pos in snapshot_fila"
            :key="pos.id"
          >
            <v-list-item-avatar size="28">
              <template v-if="_componenteInfosById(pos.id_prodotto_pianificazione).length <= 1">
                <img v-if="imgUrlPerPosizione(pos)" :src="imgUrlPerPosizione(pos)" class="color-swatch" />
                <div v-else class="color-swatch" :style="{ backgroundColor: colorePerPosizione(pos) }" />
              </template>
              <svg v-else viewBox="0 0 28 28" width="28" height="28">
                <defs>
                  <clipPath
                    v-for="(inf, idx) in _componenteInfosById(pos.id_prodotto_pianificazione)"
                    :key="`cpl-${pos.id}-${idx}`"
                    :id="`pl-${pos.id}-${idx}`"
                  >
                    <path :d="_sectorPath(idx, _componenteInfosById(pos.id_prodotto_pianificazione).length, 14, 14, 14)"/>
                  </clipPath>
                </defs>
                <g v-for="(inf, idx) in _componenteInfosById(pos.id_prodotto_pianificazione)" :key="`secl-${pos.id}-${idx}`">
                  <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="28" height="28"
                         :clip-path="`url(#pl-${pos.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                  <path v-else :d="_sectorPath(idx, _componenteInfosById(pos.id_prodotto_pianificazione).length, 14, 14, 14)"
                        :fill="inf.fallback"/>
                </g>
                <circle cx="14" cy="14" r="13.5" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
              </svg>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="text-body-2">
                {{ prodotto_labels[pos.id_prodotto_pianificazione] || `P${pos.id_prodotto_pianificazione}` }}
              </v-list-item-title>
              <v-list-item-subtitle>
                vasi {{ pos.vaso_inizio }}–{{ pos.vaso_fine }}
                ({{ pos.vaso_fine - pos.vaso_inizio + 1 }} pz.)
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                icon
                small
                color="red"
                :loading="eliminating_id === pos.id"
                :disabled="!!eliminating_id"
                @click="eliminaPosizione(pos)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider class="mb-4" />

        <!-- Form aggiunta -->
        <div class="d-flex align-center mb-3">
          <span class="text-body-2 font-weight-medium">Aggiungi piazzamento</span>
          <v-chip v-if="contenitore_fila" x-small outlined class="ml-2" color="blue-grey">
            <v-icon x-small left>mdi-circle-outline</v-icon>
            Solo Ø{{ contenitore_fila.diametro }} cm
          </v-chip>
        </div>
        <v-form ref="form_aggiungi" v-model="form_valid">
          <!-- Step 1: scegli la specie -->
          <v-select
            v-model="id_specie_filtro"
            :items="specie_in_stagione"
            item-value="id"
            item-text="nome"
            label="Specie"
            outlined
            dense
            clearable
            hide-details
            class="mb-2"
          />

          <!-- Step 2: scegli il prodotto / varietà -->
          <v-autocomplete
            v-model="form.id_prodotto_pianificazione"
            :items="prodotti_filtrati_items"
            item-value="id"
            item-text="label"
            label="Varietà / Prodotto"
            outlined
            dense
            :disabled="!id_specie_filtro"
            :rules="[(v) => !!v || 'Seleziona un prodotto']"
            clearable
            :filter="filtroAutocomplete"
            no-data-text="Nessun prodotto per questa specie"
          >
            <template #item="{ item }">
              <v-list-item-avatar size="28" class="my-1">
                <template v-if="item.component_infos && item.component_infos.length <= 1">
                  <img v-if="item.img_url" :src="item.img_url" class="color-swatch" />
                  <div v-else class="color-swatch" :style="{ backgroundColor: item.color }" />
                </template>
                <svg v-else viewBox="0 0 28 28" width="28" height="28">
                  <defs>
                    <clipPath
                      v-for="(inf, idx) in item.component_infos"
                      :key="`cpd-${item.id}-${idx}`"
                      :id="`pd-${item.id}-${idx}`"
                    >
                      <path :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"/>
                    </clipPath>
                  </defs>
                  <g v-for="(inf, idx) in item.component_infos" :key="`secd-${item.id}-${idx}`">
                    <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="28" height="28"
                           :clip-path="`url(#pd-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                    <path v-else :d="_sectorPath(idx, item.component_infos.length, 14, 14, 14)"
                          :fill="inf.fallback"/>
                  </g>
                  <circle cx="14" cy="14" r="13.5" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
                </svg>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-subtitle v-if="item.id_contenitore">
                  {{ _labelContenitore(item.id_contenitore) }}
                </v-list-item-subtitle>
                <v-list-item-title class="text-body-2">{{ item.label.replace(/\s*·?\s*Ø\d+.*$/, '').trim() }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <template #selection="{ item }">
              <div class="d-flex align-center">
                <template v-if="!item.component_infos || item.component_infos.length <= 1">
                  <img v-if="item.img_url" :src="item.img_url" class="color-swatch mr-2" />
                  <div v-else class="color-swatch mr-2" :style="{ backgroundColor: item.color }" />
                </template>
                <svg v-else class="color-swatch mr-2" viewBox="0 0 16 16" width="16" height="16">
                  <defs>
                    <clipPath
                      v-for="(inf, idx) in item.component_infos"
                      :key="`cps-${item.id}-${idx}`"
                      :id="`ps-${item.id}-${idx}`"
                    >
                      <path :d="_sectorPath(idx, item.component_infos.length, 8, 8, 8)"/>
                    </clipPath>
                  </defs>
                  <g v-for="(inf, idx) in item.component_infos" :key="`secs-${item.id}-${idx}`">
                    <image v-if="inf.img_url" :href="inf.img_url" x="0" y="0" width="16" height="16"
                           :clip-path="`url(#ps-${item.id}-${idx})`" preserveAspectRatio="xMidYMid slice"/>
                    <path v-else :d="_sectorPath(idx, item.component_infos.length, 8, 8, 8)"
                          :fill="inf.fallback"/>
                  </g>
                  <circle cx="8" cy="8" r="7.5" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
                </svg>
                {{ item.label }}
              </div>
            </template>
          </v-autocomplete>

          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.vaso_inizio"
                label="Vaso da"
                type="number"
                outlined
                dense
                :min="1"
                :max="fila.num_vasi"
                :rules="[
                  (v) => !!v || 'Richiesto',
                  (v) => v >= 1 || 'Min 1',
                  (v) => v <= fila.num_vasi || `Max ${fila.num_vasi}`,
                ]"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.vaso_fine"
                label="Vaso a"
                type="number"
                outlined
                dense
                :min="1"
                :max="fila.num_vasi"
                :rules="[
                  (v) => !!v || 'Richiesto',
                  (v) => v >= form.vaso_inizio || 'Deve essere ≥ vaso da',
                  (v) => v <= fila.num_vasi || `Max ${fila.num_vasi}`,
                ]"
              />
            </v-col>
          </v-row>

          <v-alert v-if="save_success" type="success" outlined dense class="mt-1 mb-0">
            Piazzamento salvato.
          </v-alert>

        </v-form>
      </v-card-text>

      <v-divider />
      <v-alert v-if="overlap_error" type="warning" outlined dense class="mb-0 rounded-0">
        {{ overlap_error }}
      </v-alert>
      <v-alert v-if="save_error" type="error" outlined dense class="mb-0 rounded-0">
        {{ save_error }}
      </v-alert>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Chiudi</v-btn>
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!form_valid || !!overlap_error"
          @click="salvaposizione"
        >
          Aggiungi
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import colorUtility from "../Mixin/colorUtility.js";
import specieLookup from "../Mixin/specieLookup.js";

export default {
  name: "ProdottoPicker",
  mixins: [colorUtility, specieLookup],
  props: {
    value: { type: Boolean, default: false },
    fila: { type: Object, default: null },
    file: { type: Array, default: () => [] },
    snapshot_fila: { type: Array, default: () => [] },
    snapshot_fila_precedente: { type: Array, default: () => [] },
    prodotti_pianificazione: { type: Array, default: () => [] },
    prodotto_labels: { type: Object, default: () => ({}) },
    specie: { type: Array, default: () => [] },
    contenitori: { type: Array, default: () => [] },
    loading:      { type: Boolean, default: false },
    save_error:   { type: String,  default: "" },
    save_success: { type: Boolean, default: false },
  },
  data() {
    return {
      form_valid: false,
      eliminating_id: null,
      id_specie_filtro: null,
      form: {
        id_prodotto_pianificazione: null,
        vaso_inizio: 1,
        vaso_fine: null,
      },
    };
  },
  watch: {
    value(val) {
      if (val) {
        this.resetForm();
        this._pushBackState();
      } else {
        this._cleanBackState();
      }
    },
    fila(val) {
      this._salvando = false;
      if (val) this.resetForm();
    },
    id_specie_filtro(newVal) {
      if (!newVal || !this.form.id_prodotto_pianificazione) return;
      const ancora = this.prodotti_filtrati_items.some(
        (p) => p.id === this.form.id_prodotto_pianificazione
      );
      if (!ancora) this.form.id_prodotto_pianificazione = null;
    },
    "snapshot_fila.length"(newLen, oldLen) {
      if (newLen > oldLen && this._salvando) {
        this._salvando = false;
        const occupati = this.snapshot_fila.reduce(
          (a, p) => a + (p.vaso_fine - p.vaso_inizio + 1), 0
        );
        if (this.fila && occupati >= this.fila.num_vasi && this.fila_succ) {
          this.$emit('naviga-fila', this.fila_succ);
        } else {
          this.resetForm();
          this._impostaRangeLibero();
        }
      } else if (newLen > oldLen) {
        this.resetForm();
      }
    },
    snapshot_fila(newList) {
      if (this.eliminating_id && !newList.find((p) => p.id === this.eliminating_id)) {
        this.eliminating_id = null;
      }
    },
  },
  computed: {
    fila_index() {
      if (!this.fila) return -1;
      return this.file.findIndex((f) => f.id === this.fila.id);
    },
    fila_prec() {
      return this.fila_index > 0 ? this.file[this.fila_index - 1] : null;
    },
    fila_succ() {
      return this.fila_index < this.file.length - 1 ? this.file[this.fila_index + 1] : null;
    },
    specie_disponibili() {
      return this.specie;
    },
    id_contenitore_fila() {
      if (!this.snapshot_fila.length) return null;
      const id_prod = this.snapshot_fila[0].id_prodotto_pianificazione;
      const prod = this.prodotti_pianificazione.find((p) => p.id === id_prod);
      return prod ? prod.id_contenitore : null;
    },
    contenitore_fila() {
      if (!this.id_contenitore_fila) return null;
      return this.contenitori.find((c) => c.id === this.id_contenitore_fila) || null;
    },
    specie_in_stagione() {
      const seen = new Set();
      const result = [];
      for (const p of this.prodotti_pianificazione) {
        for (const c of (p.componenti || [])) {
          if (!c.id_specie || seen.has(c.id_specie)) continue;
          seen.add(c.id_specie);
          const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
          result.push({ id: c.id_specie, nome: sp ? sp.nome : `Sp.${c.id_specie}` });
        }
      }
      return result.sort((a, b) => a.nome.localeCompare(b.nome));
    },
    prodotti_items() {
      return this.prodotti_pianificazione.map((p) => ({
        ...p,
        label: this.prodotto_labels[p.id] || `P${p.id}`,
        color: this.productColor(p),
        img_url: this._varietaImgUrl(p),
        component_infos: this._componenteInfos(p),
      }));
    },
    prodotti_filtrati_items() {
      const id_sp = this.id_specie_filtro;
      const id_cont = this.id_contenitore_fila;

      // Filtra per contenitore (coerenza diametro fila)
      const per_contenitore = id_cont
        ? this.prodotti_items.filter((p) => p.id_contenitore === id_cont)
        : this.prodotti_items;

      // Filtra per specie
      const lista = id_sp
        ? per_contenitore.filter((p) =>
            (p.componenti || []).some((c) => c.id_specie == id_sp)
          )
        : per_contenitore;

      // Applica etichetta corta quando la specie è selezionata
      const mapped = lista.map((p) => {
        if (id_sp) {
          const breve = this.labelBreve(p, id_sp);
          if (breve) return { ...p, label: breve };
        }
        return p;
      });

      // Raggruppa per diametro contenitore e deduplica per etichetta
      const gruppi = new Map();
      for (const item of mapped) {
        const m = (this.prodotto_labels[item.id] || '').match(/Ø(\d+)/);
        const diam = m ? parseInt(m[1]) : 0;
        if (!gruppi.has(diam)) gruppi.set(diam, { seen: new Set(), items: [] });
        const g = gruppi.get(diam);
        const seenKey = `${item.label}|${item.id_contenitore ?? ''}`;
        if (!g.seen.has(seenKey)) {
          g.seen.add(seenKey);
          g.items.push(item);
        }
      }

      const ordine = [...gruppi.keys()].sort((a, b) =>
        a === 0 ? 1 : b === 0 ? -1 : a - b
      );

      // Un solo gruppo: lista piatta, niente header
      if (ordine.length === 1) return gruppi.get(ordine[0]).items;

      const result = [];
      for (const diam of ordine) {
        result.push({ header: diam > 0 ? `Ø ${diam} cm` : 'Senza contenitore' });
        result.push(...gruppi.get(diam).items);
      }
      return result;
    },
    overlap_error() {
      const { vaso_inizio, vaso_fine } = this.form;
      if (!vaso_inizio || !vaso_fine) return "";
      for (const pos of this.snapshot_fila) {
        if (vaso_inizio <= pos.vaso_fine && vaso_fine >= pos.vaso_inizio) {
          const lbl = this.prodotto_labels[pos.id_prodotto_pianificazione] || `P${pos.id_prodotto_pianificazione}`;
          return `Intervallo sovrapposto con "${lbl}" (vasi ${pos.vaso_inizio}–${pos.vaso_fine})`;
        }
      }
      return "";
    },
  },
  methods: {
    _componenteInfos(prodotto) {
      const seen = new Set();
      const fallbacks = ['#EF9A9A', '#90CAF9', '#A5D6A7', '#FFE082'];
      const infos = [];
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const key = `${c.id_specie}|${id_v ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const sp = this.specie_disponibili.find(s => s.id == c.id_specie);
        let img_url = null;
        if (sp && id_v) {
          const v = (sp.variety || []).find(v => v.id == id_v);
          if (v?.id_colore) img_url = `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${v.id_colore}?t=${v.md5_colore || ''}`;
        }
        infos.push({ img_url, fallback: fallbacks[infos.length % fallbacks.length] });
        if (infos.length >= 4) break;
      }
      return infos;
    },
    _componenteInfosById(id) {
      const p = this.prodotti_pianificazione.find(x => x.id === id);
      return this._componenteInfos(p || null);
    },
    _sectorPath(i, n, r, cx, cy) {
      const a0 = (i / n) * 2 * Math.PI;
      const a1 = ((i + 1) / n) * 2 * Math.PI;
      const x0 = cx + r * Math.sin(a0);
      const y0 = cy - r * Math.cos(a0);
      const x1 = cx + r * Math.sin(a1);
      const y1 = cy - r * Math.cos(a1);
      const large = (a1 - a0) > Math.PI ? 1 : 0;
      return `M ${cx},${cy} L ${x0.toFixed(3)},${y0.toFixed(3)} A ${r},${r} 0 ${large},1 ${x1.toFixed(3)},${y1.toFixed(3)} Z`;
    },
    _varietaImgUrl(prodotto) {
      for (const c of (prodotto?.componenti || [])) {
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        if (!id_v) continue;
        const sp = this.specie_disponibili.find((s) => s.id == c.id_specie);
        if (!sp) continue;
        const varieta = (sp.variety || []).find((v) => v.id == id_v);
        if (!varieta || !varieta.id_colore) continue;
        return `${process.env.VUE_APP_API_ROOT}/specie/img_colore/${varieta.id_colore}?t=${varieta.md5_colore || ''}`;
      }
      return null;
    },
    imgUrlPerPosizione(pos) {
      const p = this.prodotti_pianificazione.find((x) => x.id === pos.id_prodotto_pianificazione);
      return this._varietaImgUrl(p || null);
    },
    productColor(prodotto) {
      if (!prodotto) return "#E8E8E8";
      if (prodotto.colore_contenitore) {
        const parsed = this.parseColorEntry(prodotto.colore_contenitore);
        if (parsed && parsed.hex) return parsed.hex;
      }
      const palette = [
        "#4CAF50", "#2196F3", "#FF9800", "#E91E63", "#9C27B0",
        "#00BCD4", "#FF5722", "#795548", "#009688", "#3F51B5",
      ];
      return palette[(prodotto.id || 0) % palette.length];
    },
    colorePerPosizione(pos) {
      const p = this.prodotti_pianificazione.find(
        (x) => x.id === pos.id_prodotto_pianificazione
      );
      return this.productColor(p || null);
    },
    labelBreve(prodotto, idSpecie) {
      const seen = new Set();
      const parti = [];
      for (const c of (prodotto.componenti || [])) {
        if (c.id_specie != idSpecie) continue;
        const key = `${c.id_serie ?? ''}|${c.id_varieta ?? ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const id_v = (c.id_varieta && c.id_varieta !== 'None') ? c.id_varieta : null;
        const id_s = (c.id_serie  && c.id_serie  !== 'None') ? c.id_serie  : null;
        if (!id_v) {
          const ns = id_s ? this.nomeSerie(c.id_specie, id_s) : null;
          parti.push(ns || 'Generico');
        } else {
          const nv = this.nomeVarieta(c.id_specie, id_v);
          const ns = this.nomeSerie(c.id_specie, id_s);
          if (nv && ns) parti.push(`${ns} · ${nv}`);
          else if (nv)  parti.push(nv);
          else          parti.push('Generico');
        }
      }
      if (!parti.length) return null;
      const cont = this.contenitori.find((c) => c.id === prodotto.id_contenitore);
      const diam = cont ? ` · Ø${cont.diametro}` : '';
      return parti.join(' / ') + diam;
    },
    _pushBackState() {
      if (!this.$vuetify.breakpoint.mobile) return;
      history.pushState({ dialog: 'picker' }, '');
      this._backHandler = () => {
        this._backHandler = null;
        this.$emit('input', false);
      };
      window.addEventListener('popstate', this._backHandler, { once: true });
    },
    _cleanBackState() {
      if (!this._backHandler) return;
      window.removeEventListener('popstate', this._backHandler);
      this._backHandler = null;
      history.back();
    },
    _labelContenitore(id) {
      const c = this.contenitori.find((c) => c.id === id);
      if (!c) return '';
      return `${c.tipo || 'Contenitore'} (Ø ${c.diametro})`;
    },
    filtroAutocomplete(item, queryText, itemText) {
      if (item.header !== undefined || item.divider) return true;
      return itemText.toLocaleLowerCase().includes(queryText.toLocaleLowerCase());
    },
    resetForm() {
      const pos_prec = this.snapshot_fila_precedente[0] || null;
      const id_prod_prec = pos_prec ? pos_prec.id_prodotto_pianificazione : null;
      const prod_prec = id_prod_prec
        ? this.prodotti_pianificazione.find((p) => p.id === id_prod_prec) || null
        : null;
      const specie_prec = prod_prec && (prod_prec.componenti || []).length
        ? prod_prec.componenti[0].id_specie
        : null;
      this.id_specie_filtro = specie_prec || null;
      this.form = {
        id_prodotto_pianificazione: id_prod_prec || null,
        vaso_inizio: 1,
        vaso_fine: this.fila ? this.fila.num_vasi : null,
      };
      this.$nextTick(() => {
        if (this.$refs.form_aggiungi) this.$refs.form_aggiungi.resetValidation();
      });
    },
    _impostaRangeLibero() {
      if (!this.fila) return;
      const sorted = [...this.snapshot_fila].sort((a, b) => a.vaso_inizio - b.vaso_inizio);
      let cursore = 1;
      for (const pos of sorted) {
        if (cursore < pos.vaso_inizio) break;
        cursore = Math.max(cursore, pos.vaso_fine + 1);
      }
      if (cursore <= this.fila.num_vasi) {
        this.form.vaso_inizio = cursore;
        this.form.vaso_fine = this.fila.num_vasi;
      }
    },
    eliminaPosizione(pos) {
      this.eliminating_id = pos.id;
      this.$emit("posizione-eliminata", { id_posizione: pos.id });
    },
    salvaposizione() {
      if (!this.$refs.form_aggiungi.validate()) return;
      if (this.overlap_error) return;
      this._salvando = true;
      this.$emit("posizione-salvata", {
        id_fila_stagione: this.fila.id,
        id_prodotto_pianificazione: this.form.id_prodotto_pianificazione,
        vaso_inizio: this.form.vaso_inizio,
        vaso_fine: this.form.vaso_fine,
      });
    },
  },
};
</script>

<style scoped>
.color-swatch {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  object-fit: cover;
}
</style>
